import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, Easing } from "remotion";
import { Backdrop, Title, Kicker, useReveal } from "../components";
import { COLORS, FONT, FONT_MONO, NEUTRAL, SAFE } from "../theme";

// Contador animado.
const Counter: React.FC<{ to: number; suffix?: string; start: number }> = ({ to, suffix = "", start }) => {
  const frame = useCurrentFrame();
  const v = interpolate(frame, [start, start + 40], [0, to], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  return (
    <span style={{ fontVariantNumeric: "tabular-nums", fontFamily: FONT_MONO }}>
      {Math.round(v)}
      {suffix}
    </span>
  );
};

const Stat: React.FC<{ value: React.ReactNode; label: string; accent: string; start: number }> = ({ value, label, accent, start }) => {
  const r = useReveal(start);
  return (
    <div
      style={{
        flex: 1,
        padding: "34px 30px",
        borderRadius: 22,
        background: NEUTRAL.glassBg,
        border: `1px solid ${NEUTRAL.line}`,
        ...r,
      }}
    >
      <div style={{ fontFamily: FONT, fontWeight: 800, fontSize: 74, color: accent, letterSpacing: -2, lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontFamily: FONT, fontWeight: 600, fontSize: 27, color: COLORS.sub, marginTop: 14 }}>
        {label}
      </div>
    </div>
  );
};

// Barras de SLA.
const SlaBars: React.FC<{ delay: number }> = ({ delay }) => {
  const frame = useCurrentFrame();
  const bars = [70, 92, 55, 80, 64, 88];
  return (
    <div style={{ display: "flex", gap: 20, alignItems: "flex-end", height: 240 }}>
      {bars.map((b, i) => {
        const h = interpolate(frame, [delay + i * 6, delay + 30 + i * 6], [0, b], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
          easing: Easing.bezier(0.16, 1, 0.3, 1),
        });
        const over = b > 85;
        return (
          <div
            key={i}
            style={{
              width: 70,
              height: `${h}%`,
              borderRadius: "10px 10px 0 0",
              background: over
                ? `linear-gradient(180deg, ${COLORS.greenLight}, ${COLORS.green})`
                : `linear-gradient(180deg, ${COLORS.blueLight}, ${COLORS.blue})`,
            }}
          />
        );
      })}
    </div>
  );
};

export const S7Value: React.FC = () => {
  return (
    <AbsoluteFill>
      <Backdrop tint="mix" />
      <AbsoluteFill style={{ padding: SAFE, paddingTop: SAFE * 0.7 }}>
        <div style={{ marginBottom: 16 }}>
          <Kicker color={COLORS.greenLight}>Control y escala</Kicker>
        </div>
        <Title size={74} maxWidth={1500}>
          Indicadores en tiempo real. Cada cliente, su propio sistema.
        </Title>

        <div style={{ display: "flex", gap: 60, marginTop: 60, alignItems: "center" }}>
          <div style={{ flex: 1.1, display: "flex", flexDirection: "column", gap: 22 }}>
            <div style={{ display: "flex", gap: 22 }}>
              <Stat value={<Counter to={100} suffix="%" start={30} />} label="Datos aislados por cliente" accent={COLORS.blueLight} start={26} />
              <Stat value={<><Counter to={24} suffix="/7" start={34} /></>} label="Seguimiento continuo" accent={COLORS.purpleLight} start={34} />
            </div>
            <div style={{ display: "flex", gap: 22 }}>
              <Stat value={<Counter to={11} start={42} />} label="Etapas configurables" accent={COLORS.greenLight} start={42} />
              <Stat value={<>SLA</>} label="Alertas de retraso por etapa" accent={COLORS.amber} start={50} />
            </div>
          </div>
          <div
            style={{
              flex: 0.9,
              padding: 40,
              borderRadius: 24,
              background: NEUTRAL.glassBg,
              border: `1px solid ${NEUTRAL.line}`,
            }}
          >
            <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: 26, color: COLORS.sub, marginBottom: 26 }}>
              Cumplimiento de SLA por etapa
            </div>
            <SlaBars delay={40} />
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};
